// [CaseDetails]/index.tsx
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Badge, Card, Container, Group, Loader, Center,
  Stack, Text, Title, SimpleGrid, Button, ActionIcon, Tooltip, Box,
  Menu, Modal, ColorSwatch,
} from '@mantine/core';
import {
  IconClockHour4, IconFileText,
  IconFiles, IconUsers, IconArrowLeft,
  IconInfoCircle, IconRefresh,
  IconBriefcase, IconChevronDown, IconArrowsExchange,
} from '@tabler/icons-react';
import Tabs from '@/components/ui/navigation/Tabs';
import {
  useLeaderPortalService, useCareCaseStatusService,
  CareCaseModelView, CareCaseStatus,
} from '@actschurch/ccms-sdk';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { tabStyle } from '@/components/ui/TabStyle';
import { toast } from '@/components/ui/providers/AlertProvider';

import NotesTab       from './Tabs/NotesTab';
import SessionTab     from './Tabs/SessionTab';
import CounseleesTab from './Tabs/CounseleesTab';
import FilesTab       from './Tabs/FilesTab';
import Avatar from '@/components/ui/data-display/Avatar';

// Rejected, Cancelled, Closed, On Hold — the only statuses leaders can set
const ALLOWED_STATUS_IDS = [-4, -3, -1, 2, 4];

function formatSDKDate(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  const date = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  if (raw.length >= 12) return `${date} ${raw.slice(8, 10)}:${raw.slice(10, 12)}`;
  return date;
}

interface InfoRowProps {
  label: string;
  value: string | number | null | undefined;
  badge?: boolean;
  badgeColor?: string;
}

function InfoRow({ label, value, badge, badgeColor }: InfoRowProps) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Group gap="xs" align="center">
      <Text size="sm" fw={600} w={110} style={{ flexShrink: 0 }}>{label}:</Text>
      {badge
        ? <Badge color={badgeColor} variant="light">{String(value)}</Badge>
        : <Text size="sm">{String(value)}</Text>
      }
    </Group>
  );
}

export default function MyCaseDetailsPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const caseID   = Number(id);

  const leaderPortalService = useLeaderPortalService();
  const statusService       = useCareCaseStatusService();

  const [caseData, setCaseData]           = useState<CareCaseModelView | null>(null);
  const [loading, setLoading]             = useState(true);
  const [activeTab, setActiveTab]         = useState<string | null>('case');
  const [statuses, setStatuses]           = useState<CareCaseStatus[]>([]);
  const [pendingStatus, setPendingStatus] = useState<CareCaseStatus | null>(null);
  const [changing, setChanging]           = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const s = await statusService.getAll();
        const list: CareCaseStatus[] = (s as any)?.data ?? s ?? [];
        setStatuses(
          list
            .filter((x) => ALLOWED_STATUS_IDS.includes(Number(x.statusID)))
            .sort(
              (a, b) =>
                ALLOWED_STATUS_IDS.indexOf(Number(a.statusID)) -
                ALLOWED_STATUS_IDS.indexOf(Number(b.statusID)),
            ),
        );
      } catch (e) {
        console.error('Error fetching case statuses:', e);
      }
    })();
  }, [statusService]);

  const fetchCase = useCallback(async (showLoading = true) => {
    if (!caseID) return;
    if (showLoading) setLoading(true);
    try {
      const data = await leaderPortalService.caseDetail(caseID);
      setCaseData(data);
    } catch (e) {
      console.error('Error fetching case:', e);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [caseID, leaderPortalService]);

  const handleRefresh = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      await fetchCase(false);
    } catch (e) {
      console.error('Error refreshing case details:', e);
    } finally {
      setLoading(false);
    }
  }, [caseID, fetchCase]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const statusColor = caseData?.statusColor ?? (caseData?.isActive ? 'green' : 'red');
  const statusLabel = caseData?.statusName ?? (caseData?.isActive ? 'Active' : 'Inactive');

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;
    setChanging(true);
    try {
      await leaderPortalService.changeCaseStatus(caseID, Number(pendingStatus.statusID));
      toast.success('Status updated', `Case is now "${pendingStatus.name}".`);
      setPendingStatus(null);
      await fetchCase(false); // no full-page loader, so tabs stay mounted
    } catch (e) {
      console.error('Error changing case status:', e);
      toast.error('Error', 'Failed to change the case status.');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading case details…</Text>
        </Stack>
      </Center>
    );
  }

  if (!caseData) {
    return <Center py="xl"><Text c="dimmed">Case not found.</Text></Center>;
  }

  return (
    <Container size="xl" py="xl">
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      <Group justify="space-between" align="center" mb="md">
        <Group gap="sm">
          <Title order={2}>Case Details</Title>
        </Group>
        <Group gap="xs" align="center">
          <Tooltip label="Refresh" withArrow>
            <ActionIcon variant="subtle" onClick={handleRefresh} loading={loading} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>

          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <Button
                variant="light"
                leftSection={<IconArrowsExchange size={16} />}
                rightSection={<IconChevronDown size={14} />}
              >
                Change Status
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Move case to</Menu.Label>
              {statuses.map((s) => (
                <Menu.Item
                  key={s.statusID}
                  disabled={s.name === caseData.statusName}
                  leftSection={<ColorSwatch color={s.color || '#868e96'} size={14} />}
                  onClick={() => setPendingStatus(s)}
                >
                  {s.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
            <Avatar radius="md" size={110}>
              <IconBriefcase size={60} />
            </Avatar>

            <Stack gap={6} style={{ flex: 1 }}>
              <Group justify="space-between" align="center" gap="sm">
                <Title order={3}>{caseData.name ?? caseData.displayName ?? '—'}</Title>
                <Badge size="lg" color={statusColor} variant="filled">{statusLabel}</Badge>
              </Group>

              {caseData.description && (
                <Group gap={6} align="flex-start" wrap="nowrap">
                  <Text size="sm" fw={700} style={{ flexShrink: 0 }}>Description:</Text>
                  <Box
                    style={{ flex: 1 }}
                    dangerouslySetInnerHTML={{ __html: caseData.description || '' }}
                  />
                </Group>
              )}

              {caseData.leaderName && (
                <Text size="sm">
                  <strong>Leader:</strong> {caseData.leaderName}
                </Text>
              )}
            </Stack>
          </Group>
        </Group>
      </Card>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="case"       leftSection={<IconInfoCircle size={16} />} style={tabStyle(activeTab === 'case')}>Overview</Tabs.Tab>
          <Tabs.Tab value="notes"      leftSection={<IconFileText   size={16} />} style={tabStyle(activeTab === 'notes')}>Notes</Tabs.Tab>
          <Tabs.Tab value="session"    leftSection={<IconClockHour4 size={16} />} style={tabStyle(activeTab === 'session')}>Session</Tabs.Tab>
          <Tabs.Tab value="counselees" leftSection={<IconUsers      size={16} />} style={tabStyle(activeTab === 'counselees')}>Counselees</Tabs.Tab>
          <Tabs.Tab value="files"      leftSection={<IconFiles      size={16} />} style={tabStyle(activeTab === 'files')}>Files</Tabs.Tab>
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="xs">
          <Tabs.Panel value="case" pt="md">
            <Title order={4} mb="md">Overview</Title>
            {caseData.description && <Text mb="md"><div dangerouslySetInnerHTML={{ __html: caseData.description }} /></Text>}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <InfoRow label="Case ID"       value={caseData.caseID} />
              <InfoRow label="Status"        value={statusLabel} badge badgeColor={statusColor} />
              <InfoRow label="Need"          value={caseData.needName} badge badgeColor={caseData.needColor ?? undefined} />
              <InfoRow label="Area"          value={caseData.areaName} badge badgeColor={caseData.areaColor ?? undefined} />
              <InfoRow label="Zone"          value={caseData.zoneName} badge badgeColor={caseData.zoneColor ?? undefined} />
              <InfoRow label="Location"      value={caseData.locationName} />
              <InfoRow label="Leader"        value={caseData.leaderName} />
              <InfoRow label="Date Opened"   value={formatSDKDate(caseData.caseDateTime)} />
              <InfoRow label="Sessions"      value={caseData.sessionCount} />
              <InfoRow label="Last Activity" value={caseData.lastActivityDisplay} />
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="notes" pt="md">
            <Title order={4} mb="md">Notes</Title>
            <NotesTab caseId={caseID} active={activeTab === 'notes'} currentUserId={user?.id ?? undefined} />
          </Tabs.Panel>

          <Tabs.Panel value="session" pt="md">
            <SessionTab caseID={caseID} active={activeTab === 'session'} currentUserId={user?.id ?? undefined} />
          </Tabs.Panel>

          <Tabs.Panel value="counselees" pt="md">
            <CounseleesTab caseID={caseID} active={activeTab === 'counselees'} />
          </Tabs.Panel>

          <Tabs.Panel value="files" pt="md">
            <Title order={4} mb="md">Files</Title>
            <FilesTab caseID={caseID} active={activeTab === 'files'} />
          </Tabs.Panel>
        </Card>
      </Tabs>

      <Modal
        opened={!!pendingStatus}
        onClose={() => !changing && setPendingStatus(null)}
        title="Change case status"
        centered
      >
        <Stack>
          <Text size="sm">Change status from:</Text>
          <Group gap="xs">
            <Badge color={statusColor} variant="filled">{statusLabel}</Badge>
            <Text size="sm">→</Text>
            {pendingStatus && (
              <Badge
                variant="filled"
                style={{ backgroundColor: pendingStatus.color || '#868e96' }}
              >
                {pendingStatus.name}
              </Badge>
            )}
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingStatus(null)} disabled={changing}>
              Cancel
            </Button>
            <Button onClick={handleConfirmStatusChange} loading={changing}>
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}